import { de, enUS, es, type Locale } from 'date-fns/locale';
import { endOfMonth, isAfter, isBefore, isSameDay, startOfDay, startOfMonth } from 'date-fns';

export type DateLike = Date | string | null | undefined;

/** Month picker value. `month` is zero-based, matching `Date#getMonth`. */
export interface MonthValue {
  month: number;
  year: number;
}

export type DatepickerModelValue = DateLike | DateLike[] | MonthValue | number;

export type DisabledDates = ((date: Date) => boolean) | Date[];

export interface DayConstraints {
  minDate?: Date | null;
  maxDate?: Date | null;
  disabledDates?: DisabledDates;
}

const DATE_FNS_LOCALES: Record<string, Locale> = { de, en: enUS, es };

export function getDateFnsLocale(locale: string): Locale {
  return DATE_FNS_LOCALES[locale] ?? enUS;
}

export function toDate(value: DateLike): Date | null {
  if (!value) return null;
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isDayOutOfBounds(date: Date, { minDate, maxDate }: DayConstraints): boolean {
  const day = startOfDay(date);
  return (
    (!!minDate && isBefore(day, startOfDay(minDate))) ||
    (!!maxDate && isAfter(day, startOfDay(maxDate)))
  );
}

/** A month is out of bounds only when none of its days lie between `minDate` and `maxDate`. */
export function isMonthOutOfBounds(
  { month, year }: MonthValue,
  { minDate, maxDate }: DayConstraints,
): boolean {
  const monthStart = new Date(year, month, 1);
  return (
    (!!minDate && isBefore(endOfMonth(monthStart), startOfDay(minDate))) ||
    (!!maxDate && isAfter(startOfMonth(monthStart), startOfDay(maxDate)))
  );
}

export function isDayDisabled(date: Date, constraints: DayConstraints): boolean {
  if (isDayOutOfBounds(date, constraints)) return true;
  const { disabledDates } = constraints;
  if (!disabledDates) return false;
  if (typeof disabledDates === 'function') return disabledDates(date);
  return disabledDates.some((disabledDate) => isSameDay(disabledDate, date));
}

/**
 * Put the time of day of `timeSource` (or the current time) on the calendar day of `day`.
 * Mirrors the previous vue-datepicker behaviour, which kept the current time for new selections.
 */
export function applyTimeOfDay(day: Date, timeSource?: Date | null): Date {
  const source = timeSource ?? new Date();
  const result = new Date(day);
  result.setHours(source.getHours(), source.getMinutes(), timeSource ? source.getSeconds() : 0, 0);
  return result;
}

export function clampDate(date: Date, { minDate, maxDate }: DayConstraints): Date {
  if (minDate && isBefore(date, minDate)) return new Date(minDate);
  if (maxDate && isAfter(date, maxDate)) return new Date(maxDate);
  return date;
}
